import { ReactNode, useEffect, useState } from "react"
import { DownOutlined, RightOutlined, InboxOutlined } from "@ant-design/icons"
import {
  formatGroupCurrency,
  formatGroupPercent,
  formatGroupRoas,
  IProjectGroup,
} from "./projectGroups.helper"
import styles from "./ProjectGroupList.module.scss"

interface ProjectGroupListProps<TCampaign> {
  groups: IProjectGroup<TCampaign>[]
  isExpandedAll: boolean
  renderGroupContent: (group: IProjectGroup<TCampaign>) => ReactNode
}

function ProjectGroupList<TCampaign>({
  groups,
  isExpandedAll,
  renderGroupContent,
}: ProjectGroupListProps<TCampaign>) {
  const [expandedGroupIds, setExpandedGroupIds] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (isExpandedAll) {
      setExpandedGroupIds(Object.fromEntries(groups.map((group) => [group.id, true])))
    } else {
      setExpandedGroupIds({})
    }
  }, [isExpandedAll])

  function toggleGroup(groupId: string) {
    setExpandedGroupIds((expanded) => ({ ...expanded, [groupId]: !expanded[groupId] }))
  }

  return (
    <div className={styles.groups}>
      {groups.map((group) => {
        const isExpanded = expandedGroupIds[group.id] ?? false
        return (
          <div key={group.id} className={styles.card}>
            <GroupHeader
              group={group}
              isExpanded={isExpanded}
              onToggle={() => toggleGroup(group.id)}
            />
            {isExpanded && <div className={styles.body}>{renderGroupContent(group)}</div>}
          </div>
        )
      })}
    </div>
  )
}

interface GroupHeaderProps<TCampaign> {
  group: IProjectGroup<TCampaign>
  isExpanded: boolean
  onToggle: () => void
}

function GroupHeader<TCampaign>({ group, isExpanded, onToggle }: GroupHeaderProps<TCampaign>) {
  return (
    <div className={styles.header} onClick={onToggle}>
      {isExpanded ? (
        <DownOutlined className={styles.chevron} />
      ) : (
        <RightOutlined className={styles.chevron} />
      )}
      <GroupImage group={group} />
      <div className={styles.titleWrap}>
        <span
          className={
            group.isUnassigned ? `${styles.title} ${styles.titleUnassigned}` : styles.title
          }
        >
          {group.title}
        </span>
        <span className={styles.badge}>
          {group.metrics.activeCampaigns}/{group.metrics.totalCampaigns}
        </span>
      </div>
      <div className={styles.kpis}>
        <GroupKpi label="Spend" value={formatGroupCurrency(group.metrics.spend)} />
        <GroupKpi label="Sales" value={formatGroupCurrency(group.metrics.sales)} />
        <GroupKpi
          label="ACOS"
          value={formatGroupPercent(group.metrics.acos)}
          valueClassName={acosClassName(group.metrics.acos)}
        />
        <GroupKpi label="ROAS" value={formatGroupRoas(group.metrics.roas)} />
      </div>
    </div>
  )
}

function GroupImage<TCampaign>({ group }: { group: IProjectGroup<TCampaign> }) {
  if (group.isUnassigned || !group.imageUrl) {
    return (
      <div className={styles.imagePlaceholder}>
        <InboxOutlined />
      </div>
    )
  }

  return <img src={group.imageUrl} alt={group.title} className={styles.image} loading="lazy" />
}

const GroupKpi: React.FC<{ label: string; value: string; valueClassName?: string }> = ({
  label,
  value,
  valueClassName,
}) => {
  return (
    <div className={styles.kpi}>
      <span className={styles.kpiLabel}>{label}</span>
      <span className={valueClassName ? `${styles.kpiValue} ${valueClassName}` : styles.kpiValue}>
        {value}
      </span>
    </div>
  )
}

function acosClassName(acos: number): string {
  if (acos <= 0) return styles.acosMuted
  if (acos < 20) return styles.acosGood
  if (acos < 30) return styles.acosWarn
  return styles.acosBad
}

export default ProjectGroupList
